import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface Teacher {
    id: string;
    name: string;
    nameEn: string;
    photo?: string;
    title?: string;
    titleEn?: string;
}

interface TeacherAttendanceStatus {
    teacherId: string;
    name: string;
    nameEn: string;
    photo?: string;
    status: 'present' | 'absent' | 'late';
    notes: string;
}

interface TeacherAttendanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    teachers: Teacher[];
    attendanceId: string;
    attendanceDate: Date;
    existingRecords?: TeacherAttendanceStatus[];
    onSaveSuccess?: () => void;
}

const TeacherAttendanceModal: React.FC<TeacherAttendanceModalProps> = ({
    isOpen,
    onClose,
    teachers,
    attendanceId,
    attendanceDate,
    existingRecords = [],
    onSaveSuccess
}) => {
    const { t, i18n } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);
    
    // 初始化教师考勤状态
    const [teacherStatuses, setTeacherStatuses] = useState<TeacherAttendanceStatus[]>(() => {
        // 如果有已存在的记录，使用已存在的记录
        if (existingRecords.length > 0) {
            return existingRecords;
        }
        
        // 否则，为每个教师创建默认状态
        return teachers.map(teacher => ({
            teacherId: teacher.id,
            name: teacher.name,
            nameEn: teacher.nameEn || teacher.name,
            photo: teacher.photo,
            status: 'absent', // 默认状态改为缺席
            notes: ''
        }));
    });

    const handleStatusChange = (teacherId: string, status: 'present' | 'absent' | 'late') => {
        setTeacherStatuses(prev => prev.map(teacher => 
            teacher.teacherId === teacherId ? {...teacher, status} : teacher
        ));
    };

    const handleNotesChange = (teacherId: string, notes: string) => {
        setTeacherStatuses(prev => prev.map(teacher => 
            teacher.teacherId === teacherId ? {...teacher, notes} : teacher
        ));
    };

    const handleSaveAttendance = async () => {
        if (!attendanceId) return;
        
        try {
            setIsSaving(true);
            
            // 准备更新的记录数据
            const records = teacherStatuses.map(teacher => ({
                teacher: doc(db, 'teachers', teacher.teacherId),
                signInTime: attendanceDate,
                signOutTime: attendanceDate, // 可以根据需要调整
                status: teacher.status,
                notes: teacher.notes
            }));
            
            // 更新考勤记录
            await updateDoc(doc(db, 'teacher_attendance', attendanceId), {
                records: records
            });
            
            // 保存成功
            if (onSaveSuccess) {
                onSaveSuccess();
            }
            
            // 关闭弹窗
            onClose();
            
        } catch (error) {
            console.error('保存考勤记录失败:', error);
            alert(t('attendance.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'present':
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            case 'absent':
                return <XCircleIcon className="h-5 w-5 text-red-500" />;
            case 'late':
                return <ClockIcon className="h-5 w-5 text-yellow-500" />;
            default:
                return null;
        }
    };

    return (
        <Dialog 
            open={isOpen} 
            onClose={onClose}
            className="relative z-50"
        >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
                    <Dialog.Title className="text-xl font-medium text-gray-900 mb-4">
                        {t('attendance.teacherAttendanceTitle')}
                    </Dialog.Title>
                    
                    {teacherStatuses.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">{t('attendance.noTeachers')}</p>
                    ) : (
                        <div className="space-y-4 mb-6">
                            {teacherStatuses.map((teacher) => (
                                <motion.div 
                                    key={teacher.teacherId}
                                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center">
                                            {teacher.photo ? (
                                                <img 
                                                    src={teacher.photo} 
                                                    alt={i18n.language === 'zh' ? teacher.name : teacher.nameEn} 
                                                    className="w-10 h-10 rounded-full object-cover mr-3"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                                    <span className="text-blue-600 text-lg">{teacher.name.charAt(0)}</span>
                                                </div>
                                            )}
                                            <div className="font-medium">
                                                {i18n.language === 'zh' ? teacher.name : teacher.nameEn}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center">
                                            {getStatusIcon(teacher.status)}
                                            <span className={`ml-1 ${
                                                teacher.status === 'present' ? 'text-green-600' : 
                                                teacher.status === 'absent' ? 'text-red-600' : 
                                                'text-yellow-600'
                                            }`}>
                                                {teacher.status === 'present' ? t('attendance.present') :
                                                teacher.status === 'absent' ? t('attendance.absent') :
                                                teacher.status === 'late' ? t('attendance.late') :
                                                teacher.status}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm text-gray-600 mb-1">
                                                {t('attendance.status')}
                                            </label>
                                            <select 
                                                className="w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                                value={teacher.status}
                                                onChange={(e) => handleStatusChange(teacher.teacherId, e.target.value as 'present' | 'absent' | 'late')}
                                            >
                                                <option value="present">{t('attendance.present')}</option>
                                                <option value="absent">{t('attendance.absent')}</option>
                                                <option value="late">{t('attendance.late')}</option>
                                            </select>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm text-gray-600 mb-1">
                                                {t('attendance.notes')}
                                            </label>
                                            <input 
                                                type="text" 
                                                className="w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder={t('attendance.notesPlaceholder')}
                                                value={teacher.notes}
                                                onChange={(e) => handleNotesChange(teacher.teacherId, e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                    
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                            onClick={onClose}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:bg-blue-400"
                            onClick={handleSaveAttendance}
                            disabled={isSaving || teacherStatuses.length === 0}
                        >
                            {isSaving ? (
                                <>
                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                                    {t('common.saving')}
                                </>
                            ) : (
                                t('common.save')
                            )}
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default TeacherAttendanceModal; 